// src/modules/cv/FrequencyLab.jsx
// Frequency Domain Lab — DFT magnitude spectrum, power profile, filter effects, concepts

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, toGray, convolve, KERNELS } from '../../utils/cvUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TABS = ['upload', 'spectrum', 'effects', 'concepts'];
const Btn = ({onClick,children,color='var(--gold)',disabled})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1}}>{children}</button>);

// Naive DFT magnitude on a small crop (64x64 for performance)
function computeDFT(id, cropSize = 64) {
  const gray = toGray(id);
  const src = gray.data;
  const W = gray.width, H = gray.height;
  const N = cropSize;
  // Extract center crop
  const sx = Math.max(0, Math.floor((W-N)/2));
  const sy = Math.max(0, Math.floor((H-N)/2));
  const patch = [];
  for (let y=0; y<N; y++) for (let x=0; x<N; x++) {
    const py=Math.min(H-1,sy+y), px=Math.min(W-1,sx+x);
    patch.push(src[(py*W+px)*4]/255);
  }
  // DFT (naive O(n^4) on 64x64 = ok)
  const mag = new Float32Array(N*N);
  let maxMag = 0;
  for (let v=0; v<N; v++) for (let u=0; u<N; u++) {
    let re=0, im=0;
    for (let y=0; y<N; y++) for (let x=0; x<N; x++) {
      const phi = 2*Math.PI*(u*x/N + v*y/N);
      re += patch[y*N+x] * Math.cos(phi);
      im -= patch[y*N+x] * Math.sin(phi);
    }
    mag[v*N+u] = Math.sqrt(re*re+im*im);
    if (mag[v*N+u] > maxMag) maxMag = mag[v*N+u];
  }
  // Shift DC to center + log scale
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = N;
  const ctx = canvas.getContext('2d');
  const imgD = ctx.createImageData(N,N);
  for (let v=0; v<N; v++) for (let u=0; u<N; u++) {
    const su=(u+N/2)%N, sv=(v+N/2)%N;
    const logMag = Math.log(1 + mag[sv*N+su]) / Math.log(1+maxMag) * 255;
    const i=(v*N+u)*4; imgD.data[i]=imgD.data[i+1]=imgD.data[i+2]=logMag; imgD.data[i+3]=255;
  }
  ctx.putImageData(imgD,0,0);
  // Radial power profile
  const profile = [];
  const half = N/2;
  for (let r=0; r<half; r++) {
    let sum=0,count=0;
    for (let v=0; v<N; v++) for (let u=0; u<N; u++) {
      const du=u-half,dv=v-half;
      const dist=Math.sqrt(du*du+dv*dv);
      if (dist>=r && dist<r+1){sum+=mag[v*N+u];count++;}
    }
    profile.push({freq:r,power:count>0?+(sum/count/maxMag*100).toFixed(2):0});
  }
  return {spectrumURL: canvas.toDataURL(), profile};
}

const SYNTH = [{name:'Checker',type:'checker'},{name:'Shapes',type:'shapes'},{name:'Gradient',type:'gradient'},{name:'Circles',type:'circles'}];

const CONCEPTS = [
  {title:'Spatial vs Frequency Domain',color:'var(--gold)',desc:'Spatial domain operates on pixel values directly. Frequency domain decomposes the image into sine/cosine waves of different frequencies. Low frequencies = large smooth areas. High frequencies = edges, details, noise.'},
  {title:'DC Component',color:'var(--cyan)',desc:'The center of the DFT spectrum (freq=0,0) is the DC component — average brightness of the image. It\'s always the brightest point in the magnitude spectrum. We show it centered using FFTShift.'},
  {title:'Nyquist & Sampling',color:'var(--violet)',desc:'The maximum frequency representable is N/2 (Nyquist frequency) for an N-pixel signal. Frequencies above Nyquist cause aliasing — they fold back and appear as lower frequencies. This is why downsampling must be preceded by low-pass filtering.'},
  {title:'Convolution Theorem',color:'var(--green)',desc:'Convolution in spatial domain = multiplication in frequency domain. This is why convolution with a large kernel is much faster when implemented as FFT → multiply → IFFT. OpenCV uses this for large kernels.'},
  {title:'Low-pass vs High-pass',color:'var(--orange)',desc:'Low-pass filter: zero out high frequencies → blurred image. High-pass filter: zero out low frequencies → edges only. Band-pass: keep a frequency band → textures at specific scale. Gaussian blur IS a low-pass filter in frequency domain.'},
  {title:'Applications',color:'var(--red)',desc:'JPEG compression uses DCT (Discrete Cosine Transform) — a relative of DFT. MRI reconstruction from k-space is inverse DFT. Audio noise removal uses frequency-domain masking. Phase-only correlation is used for sub-pixel image alignment.'},
];

export default function FrequencyLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'spectrum' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [spectrum, setSpectrum] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [effectSrc, setEffectSrc] = useState({});

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src); const id = await imgToImageData(src); setImgData(id);
    setSpectrum(null); setEffectSrc({});
    if (tab==='upload') setTab('spectrum');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('spectrum');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const runDFT = async () => {
    if (!imgData) return; setProcessing(true);
    await new Promise(r => setTimeout(r, 10));
    const result = computeDFT(imgData, 64);
    setSpectrum(result);
    setProcessing(false);
  };

  const runEffects = () => {
    if (!imgData) return;
    const lowPass = convolve(imgData, KERNELS.gaussian5, 256, 0);
    const highPass = (() => {
      const lp = lowPass;
      const dst = new Uint8ClampedArray(imgData.data.length);
      for(let i=0;i<dst.length;i+=4){dst[i]=Math.min(255,Math.max(0,128+(imgData.data[i]-lp.data[i])));dst[i+1]=Math.min(255,Math.max(0,128+(imgData.data[i+1]-lp.data[i+1])));dst[i+2]=Math.min(255,Math.max(0,128+(imgData.data[i+2]-lp.data[i+2])));dst[i+3]=255;}
      return new ImageData(dst,imgData.width,imgData.height);
    })();
    const sharp = convolve(imgData, KERNELS.sharpen, 1, 0);
    setEffectSrc({lowPass:imageDataToURL(lowPass),highPass:imageDataToURL(highPass),sharpen:imageDataToURL(sharp)});
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--gold)':'transparent'}`,background:tab===t?'var(--gold)18':'transparent',color:tab===t?'var(--gold)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('freq-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="freq-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>⬡</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Checkerboard + shapes show distinct frequency patterns</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--gold)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--gold)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div></div>
                <span style={{color:'var(--gold)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {tab==='spectrum' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DFT MAGNITUDE SPECTRUM (64×64 crop, log scale)" accent="var(--gold)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Computes the 2D DFT of the center 64×64 crop. DC component (average brightness) is shifted to center. Log scale: log(1+|F|). Bright spots = dominant frequencies. Checkerboards show diagonal lines; smooth images show concentrated center.
            </div>
            <Btn onClick={runDFT} disabled={processing}>{processing?'COMPUTING DFT (~2s)...':'COMPUTE SPECTRUM →'}</Btn>
          </Panel>
          {spectrum && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'20px',alignItems:'start'}}>
                <div>
                  <div style={{fontSize:'10px',color:'var(--t3)',textAlign:'center',marginBottom:'6px'}}>DFT SPECTRUM (log scale)</div>
                  <img src={spectrum.spectrumURL} alt="spectrum" style={{width:'200px',height:'200px',imageRendering:'pixelated',borderRadius:'3px',border:'1px solid var(--gold)44'}}/>
                  <div style={{fontSize:'9px',color:'var(--t3)',textAlign:'center',marginTop:'4px'}}>Center = DC (low freq) · Edges = high freq</div>
                </div>
                <Panel title="RADIAL POWER PROFILE" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={spectrum.profile}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="freq" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'frequency (cycles/px)',position:'insideBottom',offset:-2,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Area type="monotone" dataKey="power" stroke="var(--gold)" fill="var(--gold)" fillOpacity={.2} strokeWidth={2} name="Avg Power %"/>
                    </AreaChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="⬡">Most natural images have power concentrated at low frequencies (1/f spectrum). High-frequency content = edges, noise, textures.</InfoBox>
                </Panel>
              </div>
            </>
          )}
          {!spectrum && <InfoBox color="var(--gold)" icon="⬡">Click "COMPUTE SPECTRUM" above. DFT on 64×64 takes ~1–2s in pure JS.</InfoBox>}
        </div>
      )}

      {tab==='effects' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SPATIAL FILTERS = FREQUENCY DOMAIN OPERATIONS" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              By the convolution theorem: convolution in spatial domain = multiplication in frequency domain. Gaussian blur multiplies spectrum by a Gaussian (kills high freqs). High-pass = 1 − low-pass.
            </div>
            <Btn onClick={runEffects} color="var(--orange)">APPLY FILTER EFFECTS →</Btn>
          </Panel>
          {Object.keys(effectSrc).length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
              {[['Original',imgSrc,'var(--t2)','Unmodified'],['Low-pass (Gaussian)',effectSrc.lowPass,'var(--cyan)','Kills high freqs → blur'],['High-pass',effectSrc.highPass,'var(--violet)','Kills low freqs → edges'],['Sharpen',effectSrc.sharpen,'var(--gold)','Boosts high freqs']].map(([label,src,color,desc])=>src&&(
                <div key={label}>
                  <div style={{fontSize:'10px',color,textAlign:'center',fontWeight:700,marginBottom:'4px'}}>{label}</div>
                  <img src={src} alt={label} style={{width:'100%',borderRadius:'3px',border:`1px solid ${color}44`}}/>
                  <div style={{fontSize:'9px',color:'var(--t3)',textAlign:'center',marginTop:'4px'}}>{desc}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab==='concepts' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {CONCEPTS.map(({title,color,desc})=>(
            <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
              <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
              <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      )}

      {!imgData && tab!=='upload' && tab!=='concepts' && <InfoBox color="var(--gold)" icon="⬡">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
